/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import ReactPaginate from 'react-paginate';
import Spacer from '../components/Spacer';
import { getBlogPosts } from '../config/blogs';

// Sort blog posts by date (newest first)
const ITEMS_PER_PAGE = 5;

function Blog() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [itemOffset, setItemOffset] = useState(0);

  // Get current page and tag from URL
  const query = new URLSearchParams(location.search);
  const currentPage = parseInt(query.get('page') || '1', 10);
  const initialTag = query.get('tag') || null;

  // Set initial state for selectedTag
  const [selectedTag, setSelectedTag] = useState(initialTag);

  // Sort and paginate blog posts
  const sortedBlogPosts = [...getBlogPosts()].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Filter posts by selected tag
  const filteredBlogPosts = selectedTag
    ? sortedBlogPosts.filter((post) => post.tags.includes(selectedTag))
    : sortedBlogPosts;

  const endOffset = itemOffset + ITEMS_PER_PAGE;
  const currentPosts = filteredBlogPosts.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(filteredBlogPosts.length / ITEMS_PER_PAGE);

  // Handle page changes
  const handlePageClick = (event) => {
    const newOffset = (event.selected * ITEMS_PER_PAGE) % filteredBlogPosts.length;
    setItemOffset(newOffset);
    const tagQuery = selectedTag ? `&tag=${selectedTag}` : '';
    history.push(`?page=${event.selected + 1}${tagQuery}`);
  };

  // Handle tag click
  const handleTagClick = (tag) => {
    if (selectedTag === tag) {
      setSelectedTag(null);
      history.push('?page=1');
    } else {
      setSelectedTag(tag);
      setItemOffset(0); // Reset to first page
      history.push(`?page=1&tag=${tag}`);
    }
  };

  // Update itemOffset when currentPage or selectedTag changes
  useEffect(() => {
    const newOffset = ((currentPage - 1) * ITEMS_PER_PAGE) % filteredBlogPosts.length;
    setItemOffset(newOffset);
  }, [currentPage, filteredBlogPosts.length]);

  // Reset the selectedTag and itemOffset when the url doesn't have tag or page
  useEffect(() => {
    if (!location.search.includes('tag') && !location.search.includes('page')) {
      setSelectedTag(null);
      setItemOffset(0);
    }
  }, [location.search]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: t('blog.title'),
            description: t('blog.description'),
            url: 'https://cvtherapy.org/blog',
            blogPost: currentPosts.map((post) => ({
              '@type': 'BlogPosting',
              headline: t(`blog.posts.${post.id}.title`),
              datePublished: new Date(post.date).toISOString(),
              author: post.author,
              description: t(`blog.posts.${post.id}.description`),
              url: `https://cvtherapy.org${post.path}`,
            })),
          })}
        </script>
      </Helmet>
      <Spacer />
      <div className="container" style={{ minHeight: 1000 }}>
        <div className="row">
          <div className="col-12">
            <h1 className="display-4 text-center text-primary">{t('blog.title')}</h1>
            {selectedTag && (
              <p className="text-center text-muted small">
                {t('blog.showing')}
                {' '}
                {filteredBlogPosts.length}
                {' '}
                {t('blog.filteredOf')}
                {' '}
                {sortedBlogPosts.length}
                {' '}
                {t('blog.totalPosts')}
              </p>
            )}
            {currentPosts.map((post) => (
              <div key={post.path} className="card mb-4 rounded-3">
                <div className="card-body">
                  <h2 className="h4 text-primary">
                    <Link to={post.path} className="text-decoration-none">
                      {t(`blog.posts.${post.id}.title`)}
                    </Link>
                  </h2>
                  <div className="text-muted mb-2">
                    {t('blog.publishedOn')}
                    {' '}
                    {new Date(post.date).toLocaleDateString()}
                    {' '}
                    |
                    {' '}
                    {post.author}
                  </div>
                  <p>{t(`blog.posts.${post.id}.description`)}</p>
                  <div className="mb-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`badge me-2 ${selectedTag === tag ? 'bg-primary text-white' : 'bg-secondary'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleTagClick(tag)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleTagClick(tag);
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link to={post.path} className="btn btn-warning btn-sm text-white">
                    {t('blog.readMore')}
                  </Link>
                </div>
              </div>
            ))}

            {filteredBlogPosts.length === 0 && (
              <p className="text-primary">{t('blog.noPosts')}</p>
            )}

            {pageCount > 1 && (
              <ReactPaginate
                breakLabel="..."
                nextLabel="&raquo;"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount}
                previousLabel="&laquo;"
                renderOnZeroPageCount={null}
                containerClassName="pagination justify-content-center"
                pageClassName="page-item"
                pageLinkClassName="page-link text-black"
                previousClassName="page-item"
                previousLinkClassName="page-link text-black"
                nextClassName="page-item"
                nextLinkClassName="page-link text-black"
                breakClassName="page-item"
                breakLinkClassName="page-link text-black"
                activeClassName="active"
                activeLinkClassName="bg-primary border-primary text-white"
                forcePage={currentPage - 1}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Blog;
